// GameResultsModal.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, AlertCircle, TrendingUp, Trophy, AlertTriangle, Save, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { getAuth } from 'firebase/auth';
import { saveMessage } from '../../utils/db-messaging';
import { preprocessMarkdown, tableComponents, applyStandardReplacements } from './tableUtils';

/**
 * This component creates a modal popup that displays AI predictions for sports games
 * with the ability to save the prediction to the user's chat history
 */
const GameResultsModal = ({ 
  isOpen, 
  onClose, 
  predictionState,
  remainingSpins,
  isBasicUser,
  resetTime
}) => {
  // State
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [savingToHistory, setSavingToHistory] = useState(false);
  const [savedToHistory, setSavedToHistory] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  
  // Refs
  const modalRef = useRef(null);
  
  // Animation variants
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: { 
        type: 'spring', 
        damping: 25, 
        stiffness: 300,
        delay: 0.1
      } 
    },
    exit: { 
      opacity: 0, 
      scale: 0.95,
      y: 20,
      transition: { duration: 0.2 } 
    }
  };
  
  // Animation variants for confirmation dialog
  const confirmationVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        type: 'spring', 
        damping: 30, 
        stiffness: 500
      } 
    },
    exit: { 
      opacity: 0, 
      scale: 0.9,
      transition: { duration: 0.15 } 
    }
  };
  
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.15 } }
  };

  // Handle body scroll locking
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll when modal opens
      document.body.style.overflow = 'hidden';
      
      // Set mounted state - helps with mobile rendering
      setIsMounted(true);
    } else {
      // Restore scroll when modal closes
      document.body.style.overflow = '';
      
      // Reset state on close
      setTimeout(() => {
        setIsMounted(false);
        setShowConfirmation(false);
        setSavedToHistory(false);
      }, 300); // Match exit animation duration
    }
    
    return () => {
      // Ensure scroll is restored on unmount
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle clicks outside the modal
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!showConfirmation && modalRef.current && !modalRef.current.contains(event.target)) {
        handleCloseRequest();
      }
    };

    if (isOpen) {
      // Use both mousedown and touchstart for better mobile experience
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen, showConfirmation]);

  // Function to save prediction to chat history
  const saveToHistory = async () => {
    if (!predictionState.results) return;
    
    setSavingToHistory(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error("You must be logged in to save predictions");
      }
  
      // Create a title for the conversation based on the sport
      const sportName = predictionState.selectedSport || "Game";
      
      // Enhanced extraction logic - focus on the latter part of the content
      let predictionText = predictionState.results;
      
      // Take the last 2/3 of the content where conclusions are typically found
      const contentLength = predictionState.results.length;
      const startPosition = Math.floor(contentLength / 3);
      const latterContent = predictionState.results.substring(startPosition);
      
      // Look for prediction patterns in the latter content first
      const predictionPatterns = [
        /Prediction:.*?\.(?:\s|\n|$)/i,
        /Winner:.*?\.(?:\s|\n|$)/i,
        /Recommendation:.*?\.(?:\s|\n|$)/i,
        /.*?predicted winner.*?\.(?:\s|\n|$)/i,
        /.*?recommend.*?\.(?:\s|\n|$)/i
      ];
      
      // Try each pattern on the latter content
      let match = null;
      
      for (const pattern of predictionPatterns) {
        match = latterContent.match(pattern);
        if (match && match[0]) {
          predictionText = match[0].trim();
          break;
        }
      }
      
      // If no match in latter content, try the full content as a fallback
      if (!match) {
        for (const pattern of predictionPatterns) {
          match = predictionState.results.match(pattern);
          if (match && match[0]) {
            predictionText = match[0].trim();
            break;
          }
        }
      }
      
      // Add a prefix if the extracted text doesn't have one
      if (!predictionText.toLowerCase().includes('prediction:') && 
          !predictionText.toLowerCase().includes('winner:') &&
          !predictionText.toLowerCase().includes('recommendation:')) {
        predictionText = `${sportName} Prediction: ${predictionText}`;
      }
  
      // Save the message to create a new conversation
      await saveMessage(
        user.uid, 
        {
          text: predictionText,
          sender: 'assistant'
        }
      );
      
      // Set saved state to show success message
      setSavedToHistory(true);
      
      // Reset saved state after a delay
      setTimeout(() => {
        setSavedToHistory(false);
      }, 3000);
    } catch (error) {
      console.error("Error saving prediction:", error);
      // You could show an error notification here
    } finally {
      setSavingToHistory(false);
    }
  };

  // Handle close request - shows confirmation instead of closing immediately
  const handleCloseRequest = () => {
    // If no results yet, just close
    if (!predictionState.results) {
      confirmClose();
      return;
    }
    
    // If already saved, just close
    if (savedToHistory) {
      confirmClose();
      return;
    }
    
    // Otherwise show confirmation dialog
    setShowConfirmation(true);
  };

  // Confirm close - actually closes the modal
  const confirmClose = () => {
    if (onClose) {
      onClose();
    }
    setShowConfirmation(false);
  };

  // Cancel close - just hides the confirmation
  const cancelClose = () => {
    setShowConfirmation(false);
  };

  // Get current date for display
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // If not open and not mounted, don't render anything
  if (!isOpen && !isMounted) return null;

  // Extract confidence if available in the prediction results
  let confidence = null;
  if (predictionState.results) {
    const confidenceMatch = predictionState.results.match(/confidence:\s*(\d+(?:\.\d+)?)%/i) || 
                        predictionState.results.match(/(\d+(?:\.\d+)?)%\s*confidence/i);
    
    if (confidenceMatch && confidenceMatch[1]) {
      confidence = parseFloat(confidenceMatch[1]);
    }
  }

  // Calculate confidence indicator color
  const getConfidenceColor = () => {
    if (!confidence) return 'text-slate-400';
    
    if (confidence >= 80) return 'text-emerald-400';
    if (confidence >= 60) return 'text-emerald-300';
    if (confidence >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <AnimatePresence>
      {(isOpen || isMounted) && (
        <div className="fixed inset-0 z-[99999]">
          <motion.div 
            variants={overlayVariants}
            initial="hidden"
            animate={isOpen ? "visible" : "exit"}
            exit="exit"
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
          />
          
          <div className="fixed inset-0 z-[999999] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <motion.div
                ref={modalRef}
                variants={modalVariants}
                initial="hidden"
                animate={isOpen ? "visible" : "exit"}
                exit="exit"
                className="bg-black/90 backdrop-blur-lg border border-white/10 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl relative mx-auto"
              >
                {/* Header */}
                <div className="p-4 border-b border-white/[0.05] flex items-center justify-between bg-white/[0.02]">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center h-9 w-9 bg-white/[0.05] rounded-md">
                      <Trophy className="w-5 h-5 text-emerald-400/80" />
                    </div>
                    <div>
                      <h3 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase">
                        {predictionState.selectedSport || "Game"} Prediction
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-white/50 font-mono">{currentDate}</p>
                        {confidence && (
                          <span className="text-[11px] px-2 py-0.5 rounded bg-white/[0.02] border border-white/[0.05] font-mono whitespace-nowrap">
                            <span className={getConfidenceColor()}>{confidence}%</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseRequest}
                    className="p-2 text-white/40 hover:text-white/80 transition-colors"
                    aria-label="Close prediction"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Content area */}
                <div className="overflow-y-auto p-0" style={{ maxHeight: "calc(80vh - 70px)" }}>
                  {!predictionState.results ? (
                    <div className="flex flex-col items-center justify-center p-10">
                      <Loader2 className="w-10 h-10 text-emerald-400/80 animate-spin mb-4" />
                      <p className="text-white/70 font-mono text-sm">Generating Prediction...</p>
                      <p className="text-xs text-white/30 mt-2">Building analysis based on real-time data</p>
                      <div className="mt-6 p-4 bg-white/[0.02] backdrop-blur-sm rounded-lg border border-white/[0.05]">
                        <p className="text-xs text-white/50">
                          <span className="text-emerald-400/80">TIP:</span> Once complete, you can save this prediction to your chat history
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6">
                      {/* Save section with better visibility */}
                      <div className="mb-6 p-4 bg-white/[0.02] backdrop-blur-sm border border-emerald-500/20 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-[10px] font-medium text-white/30 uppercase tracking-wider mb-1">Save this prediction</h4>
                            <p className="text-xs text-white/50">Add to your chat history to reference later</p>
                          </div>
                          <button
                            onClick={saveToHistory}
                            disabled={savingToHistory || savedToHistory}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200 ml-4 text-xs uppercase tracking-wider ${
                              savedToHistory
                                ? 'bg-emerald-500/20 text-emerald-400/80 border border-emerald-500/30'
                                : savingToHistory
                                  ? 'bg-white/[0.02] text-white/40 cursor-wait'
                                  : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400/80 hover:text-emerald-400/90 border border-emerald-500/20 hover:border-emerald-500/30'
                            }`}
                          >
                            {savedToHistory ? (
                              <>
                                <Check className="w-4 h-4" />
                                <span>Saved</span>
                              </>
                            ) : savingToHistory ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Saving</span>
                              </>
                            ) : (
                              <>
                                <Save className="w-4 h-4" />
                                <span>Save</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Statistics insights header */}
                      <div className="mb-4 pb-4 border-b border-white/[0.05] flex items-center gap-2">
                        <TrendingUp className="text-emerald-400/80 w-5 h-5" />
                        <h3 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase">Statistical Analysis</h3>
                      </div>
                      
                      {/* Render the AI response using ReactMarkdown */}
                      <div className="prose prose-invert prose-emerald max-w-none">
                        {predictionState.results && (
                          <div className="markdown-content">
                            <ReactMarkdown
                              components={{
                                // Terminal-style headers
                                h1: ({node, ...props}) => <h1 className="text-xl font-medium tracking-[0.15em] text-white/80 uppercase mt-6 mb-4 text-left" {...props} />,
                                h2: ({node, ...props}) => <h2 className="text-lg font-medium tracking-[0.1em] text-white/70 uppercase mt-5 mb-3 text-left" {...props} />,
                                h3: ({node, ...props}) => <h3 className="text-base font-medium tracking-wider text-white/60 uppercase mt-4 mb-2 text-left" {...props} />,
                                
                                // Add the table components
                                ...tableComponents,
                                
                                // Keep your existing styling for lists, etc.
                                ul: ({node, ...props}) => <ul className="my-3 pl-6 space-y-2 text-left list-none" {...props} />,
                                ol: ({node, ...props}) => <ol className="my-3 pl-6 space-y-2 list-decimal text-left" {...props} />,
                                li: ({node, ...props}) => {
                                  const text = String(props.children);
                                  
                                  // Check if this is a feature item with a label
                                  if (text.includes(':**')) {
                                    const [label, content] = text.split(':**');
                                    if (label && content) {
                                      return (
                                        <li className="text-slate-300 text-left flex items-start mb-2">
                                          <span className="text-emerald-400 font-bold mr-2">•</span>
                                          <div>
                                            <span className="text-emerald-400 font-bold">{label}:</span>
                                            <span>{content}</span>
                                          </div>
                                        </li>
                                      );
                                    }
                                  }
                                  
                                  return (
                                    <li className="text-slate-300 text-left flex items-start mb-2">
                                      <span className="text-emerald-400 font-bold mr-2">•</span>
                                      <span>{props.children}</span>
                                    </li>
                                  );
                                },
                                
                                // Keep other styling with premium design
                                strong: ({node, ...props}) => <strong className="font-medium text-emerald-400/80" {...props} />,
                                em: ({node, ...props}) => <em className="text-white/70 italic" {...props} />,
                                code: ({node, inline, ...props}) =>
                                  inline
                                    ? <code className="px-1.5 py-0.5 bg-white/[0.05] rounded text-emerald-400/80 text-xs font-mono" {...props} />
                                    : <code className="block p-3 bg-white/[0.05] rounded-md text-emerald-400/80 text-xs font-mono overflow-x-auto my-3 border border-white/[0.05]" {...props} />,
                                blockquote: ({node, ...props}) =>
                                  <blockquote className="pl-4 border-l-2 border-emerald-500/20 text-white/50 italic my-4 text-left" {...props} />
                              }}
                            >
                              {/* Use the utility functions to process the markdown */}
                              {preprocessMarkdown(applyStandardReplacements(predictionState.results))}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>


                      {/* Disclaimer footer */}
                      <div className="mt-6 pt-4 border-t border-white/[0.05] text-[11px] text-white/30 font-mono">
                        <p>This prediction is based on statistical analysis and should be used for informational purposes only. Sports outcomes involve many unpredictable factors.</p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>

          {/* Confirmation Dialog */}
          <AnimatePresence>
            {showConfirmation && (
              <div className="fixed inset-0 z-[9999999] flex items-center justify-center p-4">
                <motion.div
                  variants={confirmationVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="bg-black/90 backdrop-blur-lg border border-white/10 rounded-lg p-4 shadow-lg max-w-sm w-full"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className="w-6 h-6 text-white/50 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase">Close Analysis?</h3>
                      <p className="text-white/70 text-sm mt-1">
                        {savedToHistory
                          ? "Your prediction has been saved. Are you sure you want to close?"
                          : "This prediction hasn't been saved yet. What would you like to do?"}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end">
                    {!savedToHistory ? (
                      <>
                        <button
                          onClick={() => {
                            saveToHistory();
                            setTimeout(() => {
                              confirmClose();
                            }, 1500);
                          }}
                          className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/20 hover:border-emerald-500/30 text-emerald-400/80 hover:text-emerald-400/90 rounded-md text-xs uppercase tracking-wider transition-all duration-200 flex items-center gap-2"
                        >
                          <Save className="w-4 h-4" />
                          Save & Close
                        </button>
                        <button
                          onClick={confirmClose}
                          className="px-4 py-2 bg-white/[0.02] hover:bg-white/[0.03] border border-white/[0.05] hover:border-white/[0.08] text-white/70 hover:text-white/80 rounded-md text-xs uppercase tracking-wider transition-all duration-200"
                        >
                          Close
                        </button>
                        <button
                          onClick={cancelClose}
                          className="px-4 py-2 bg-white/[0.02] hover:bg-white/[0.03] border border-white/[0.05] hover:border-white/[0.08] text-white/70 hover:text-white/80 rounded-md text-xs uppercase tracking-wider transition-all duration-200"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={confirmClose}
                          className="px-4 py-2 bg-white/[0.02] hover:bg-white/[0.03] border border-white/[0.05] hover:border-white/[0.08] text-white/70 hover:text-white/80 rounded-md text-xs uppercase tracking-wider transition-all duration-200"
                        >
                          Close
                        </button>
                        <button
                          onClick={cancelClose}
                          className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/20 hover:border-emerald-500/30 text-emerald-400/80 hover:text-emerald-400/90 rounded-md text-xs uppercase tracking-wider transition-all duration-200"
                        >
                          Keep Reading
                        </button>
                      </>
                    )}
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </div>
      )}
    </AnimatePresence>
  );
};

export default GameResultsModal;